import type {
    DashboardBaseObj,
    DashboardDataDef,
    DashboardLayout,
    DashboardObj,
    DashboardSaveObj,
    DashboardResultObj,
} from "@/lib/models";
import { AxiosError, AxiosInstance } from "axios";
import { Backend, DashboardBackend } from "@/lib/backend";
import * as Backend2 from "@/lib/backend/backend2";
import * as rest from "@/lib/backend/restApi";
import * as t from "@/lib/backend/dashboards2.generated.types";
import * as restAxios from "@/lib/backend/restApiAxios";

import { publish, MSG_TYPES, subscribe } from "@/lib/pubSub";
import type { QueryParams } from "@/lib/backend/restApi";

type IDashboardBasicInfoEx = t.IDashboardBasicInfo & {
    created_at: string;
    created_by: string;
    org_uuid: string;
    public: boolean;
    data_def: DashboardDataDef;
    layout_def: DashboardLayout;
};

// Helper function for constructing URL query string from given object
function toQueryString(obj: QueryParams | undefined): string {
    obj = obj || {};
    const parts: string[] = [];
    for (const i in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, i)) {
            parts.push(encodeURIComponent(i) + "=" + encodeURIComponent(obj[i]));
        }
    }
    return parts.join("&");
}

const {
    // Common instance of axios for setting interceptors and such, but NOT making calls
    restClient,
    // Common REST client object - should be used for making calls
    getInternalAxiosInstance,
} = (() => {
    // initialize instances inside this function to hide the implementation from outside
    const res = new restAxios.ApiClientAxios();
    const getInternalAxiosInstance = () => res.getAxiosInstance();
    const restClient: rest.ApiClient = res;
    return {
        restClient,
        getInternalAxiosInstance,
    };
})();

// Transform dashboard object as returned by the backend to DashboardBaseObj
function toDashboardBaseObj(ds: IDashboardBasicInfoEx): DashboardBaseObj {
    return {
        active: ds.active,
        created_at: new Date(ds.created_at),
        created_by: ds.created_by,
        is_system: ds.is_system,
        org_uuid: ds.org_uuid,
        public: ds.public,
        tags: ds.tags,
        title: ds.title,
        uuid: ds.uuid,
    };
}

// Transform dashboard object as returned by the backend to DashboardObj
function toDashboardObj(ds: IDashboardBasicInfoEx): DashboardObj {
    return {
        active: ds.active,
        created_at: new Date(ds.created_at),
        created_by: ds.created_by,
        data_def: ds.data_def,
        is_system: ds.is_system,
        layout_def: ds.layout_def,
        org_uuid: ds.org_uuid,
        public: ds.public,
        tags: ds.tags,
        title: ds.title,
        uuid: ds.uuid,
    };
}

/**
 * Implementing REST manufacturing backend.
 */
export class RestDashboardBackend implements DashboardBackend {
    parent: RestBackend;

    constructor(parent: RestBackend) {
        this.parent = parent;
    }

    async getDashboardsAsync(): Promise<DashboardBaseObj[]> {
        const result = await restClient.get("/api/v1.0/dashboards");
        return result.data.map(toDashboardBaseObj);
    }

    getDashboards(done: (dashboards: DashboardBaseObj[]) => void, err: (str: string) => void): void {
        restClient
            .get("/api/v1.0/dashboards")
            .then(result => {
                done(result.data.map(toDashboardBaseObj));
            })
            .catch(error => {
                this.parent._checkAuthError(error);
                console.log(error);
                err("Error retrieving dashboard list");
            });
    }

    async getSingleDashboardAsync(uuid: string): Promise<DashboardObj> {
        const result = await restClient.get("/api/v1.0/dashboards/" + uuid + "/definition");
        return toDashboardObj(result.data);
    }

    getSingleDashboard(uuid: string, done: (dashboard: DashboardObj) => void, err: (str: string) => void): void {
        restClient
            .get("/api/v1.0/dashboards/" + uuid + "/definition")
            .then(result => {
                done(toDashboardObj(result.data));
            })
            .catch(error => {
                this.parent._checkAuthError(error);
                console.log(error);
                err("Error retrieving dashboard");
            });
    }

    executeDashboard(
        uuid: string,
        from: number,
        to: number,
        params: QueryParams | undefined,
        done: (dashboard: DashboardResultObj) => void,
        err: (str: string) => void
    ): void {
        params = params || {};
        if (from > 0) {
            params.from = from;
        }
        if (to > 0) {
            params.to = to;
        }
        restClient
            .get("/api/v1.0/dashboards/" + uuid, params)
            .then(result => {
                done(result.data);
            })
            .catch(error => {
                this.parent._checkAuthError(error);
                console.log(error);
                err("Error executing dashboard");
            });
    }

    getSingleDashboardPublic(uuid: string, done: (dashboard: DashboardObj) => void, err: (str: string) => void): void {
        restClient
            .get("/api/v1.0/dashboards/" + uuid + "/definition-public")
            .then(result => {
                done(toDashboardObj(result.data));
            })
            .catch(error => {
                this.parent._checkAuthError(error);
                console.log(error);
                err("Error retrieving dashboard");
            });
    }

    executeDashboardPublic(
        uuid: string,
        from: number,
        to: number,
        params: QueryParams | undefined,
        done: (dashboard: DashboardResultObj) => void,
        err: (str: string) => void
    ): void {
        params = params || {};
        if (from > 0) {
            params.from = from;
        }
        if (to > 0) {
            params.to = to;
        }
        restClient
            .get("/api/v1.0/dashboards/" + uuid + "/public", params)
            .then(result => {
                done(result.data);
            })
            .catch(error => {
                this.parent._checkAuthError(error);
                console.log(error);
                err("Error executing dashboard");
            });
    }

    // execute given dashboard definition
    executeDashboardDef(
        layout_def: DashboardLayout,
        data_def: unknown,
        params: QueryParams | undefined,
        done: (dashboard: DashboardResultObj) => void,
        err: (str: string) => void
    ): void {
        const url_query = toQueryString(params);
        restClient
            .post("/api/v1.0/dashboards/draft?" + url_query, { layout_def, data_def })
            .then(result => {
                done(result.data);
            })
            .catch(error => {
                this.parent._checkAuthError(error);
                console.log(error);
                err("Error executing dashboard def");
            });
    }

    // save new dashboard
    insertDashboard(data: DashboardSaveObj, done: (uuid: string) => void, err: (str: string) => void): void {
        restClient
            .post("/api/v1.0/dashboards", data)
            .then(result => {
                done(result.data.uuid);
            })
            .catch(error => {
                this.parent._checkAuthError(error);
                console.log(error);
                err("Error storing dashboard");
            });
    }

    updateDashboard(uuid: string, data: DashboardSaveObj, done: () => void, err: (str: string) => void): void {
        restClient
            .put("/api/v1.0/dashboards/" + uuid, data)
            .then(() => {
                done();
            })
            .catch(error => {
                this.parent._checkAuthError(error);
                console.log(error);
                err("Error storing dashboard");
            });
    }
}

export class RestBackend implements Backend {
    dash: DashboardBackend;
    replacements_map: Record<string, string> | null = null;

    constructor() {
        this.dash = new RestDashboardBackend(this);
        Backend2.init(restClient);
        // set up handling of 401 requests
        this.getAxiosInstance().interceptors.response.use(undefined, handleErrorInterception);
        subscribe(MSG_TYPES.auth_token_refreshed, async (payload: { token: string }) => this.initAuth(payload.token));
        subscribe(MSG_TYPES.logout, async () => this.clrAuth());
    }

    getAxiosInstance(): AxiosInstance {
        return getInternalAxiosInstance();
    }

    initAuth(token: string): void {
        restClient.setRequestHeader("Authorization", "Bearer " + token);
        Backend2.getBackend().setRequestHeader("Authorization", "Bearer " + token);
    }

    clrAuth(): void {
        restClient.setRequestHeader("Authorization", null);
        Backend2.getBackend().setRequestHeader("Authorization", null);
    }

    // if we got error with auth, then clear auth
    _checkAuthError(error: AxiosError) {
        if (error.response && error.response.status && forcesLogoutOnHttpStatus(error.response.status)) {
            // our token expired, let's log out on client side as well
            publish(MSG_TYPES.logout_request, {});
            publish(MSG_TYPES.redirect_to_login, {});
        }
    }
}

/** Only 401 means the session is invalid. Transient 503/504 must not log the user out. */
export function forcesLogoutOnHttpStatus(status: number): boolean {
    return status === 401;
}

/** Error responses do not always carry our JSON error body - proxies and gateways
 * answer with plain text or HTML - so the payload shape is verified at runtime.
 */
export function serverErrorMessage(data: unknown): string {
    if (typeof data === "object" && data !== null) {
        const message = (data as { message?: unknown }).message;
        if (typeof message === "string" && message !== "") {
            return message;
        }
    }
    return "Server error";
}

/** This function handles HTTP errors */
function handleErrorInterception(error: AxiosError) {
    const response = error.response;
    if (response) {
        // detect unauthorized and redirect to login page
        if (forcesLogoutOnHttpStatus(response.status)) {
            if (window.location.pathname !== "/login") {
                publish(MSG_TYPES.logout_request, {});
                publish(MSG_TYPES.redirect_to_login, {});
            }
        }
        if (response.status >= 400) {
            return Promise.reject(new Error(serverErrorMessage(response.data)));
        }
    } else {
        console.log(error);
    }
    return Promise.reject(error);
}

/** This utility method loads file from given url and
 * opens it in new tab. This has to be done in a separate function in order to inject
 * proper headers to fetch files. Otherwise teh request might get re-routed to wrong gui server.
 */
export async function downloadAndOpenFile(url: string) {
    const response = await fetch(url, { headers: rest.COMMON_HEADERS });
    const blob = await response.blob();
    const _url = window.URL.createObjectURL(blob);
    window.open(_url, "_blank")?.focus();
}
